import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useMutation } from '@apollo/client';

import { ConfirmDialog, captureException } from '@playbookux/playbook-web-core';
import {
  ContainerApproval,
  Description,
  Dropdown,
  ErrorMessage,
  Link,
  LinkDanger,
  TextEmail,
  Row,
  Column,
  Table,
  Body,
  Header,
  HeaderCell,
} from './styles';

import {
  STATUS,
  GET_COMPANY_INVITES,
  UPDATE_COMPANY_INVITES,
} from '../graphql';

const statusOptions = [
  { key: 1, text: 'User', value: 'user' },
  { key: 2, text: 'Admin', value: 'admin' },
];

const DEFAULT_ROLE = 'user';
const STATUS_REJECTED = 'rejected';
const STATUS_ACCEPTED = 'accepted';

const CompanyInvites = (props) => {
  const {
    data,
    companyId,
  } = props;

  const [showConfirm, setShowConfirm] = useState(false);
  const [currentInviteId, setCurrentInviteId] = useState(false);
  const [dropdownValue, setDropdownValue] = useState({});
  const [error, setError] = useState(undefined);

  const [updateCompanyInvite] = useMutation(UPDATE_COMPANY_INVITES, {
    refetchQueries: [{
      query: GET_COMPANY_INVITES,
      variables: {
        companyId,
        status: STATUS,
      },
      skip: !companyId,
    }],
    awaitRefetchQueries: true,
  });

  const handleUpdateInvite = async (inviteId, status, role) => {
    try {
      await updateCompanyInvite({
        variables: {
          attributes: {
            inviteId,
            status,
            role,
          },
        },
      });
    } catch (err) {
      captureException('HANDLE_UPDATE_INVITE_FAILED', err);
      setError(err.graphQLErrors[0].message);
    }
  };

  const handleChangeDropdown = (inviteId, value) => {
    const newState = {
      ...dropdownValue,
      [inviteId]: value.value,
    };
    setDropdownValue(newState);
  };

  const handleOpenInviteDialog = (inviteId) => {
    setCurrentInviteId(inviteId);
    setShowConfirm(true);
  };

  const handleCloseDialog = () => {
    setShowConfirm(false);
  };

  const handleRemoveInvite = () => {
    const currentValue = dropdownValue[currentInviteId] || DEFAULT_ROLE;
    handleUpdateInvite(currentInviteId, STATUS_REJECTED, currentValue);
    setShowConfirm(false);
  };

  const handleApproveInvite = (inviteId) => {
    const currentValue = dropdownValue[inviteId] || DEFAULT_ROLE;
    handleUpdateInvite(inviteId, STATUS_ACCEPTED, currentValue);
  };

  const {
    companyInvites,
  } = data;

  return (
    <>
      {(error) && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}

      {_.isEmpty(data.companyInvites) ? (
        <ContainerApproval>
          <Description textAlign>
            You don&apos;t have anyone to approve
          </Description>
        </ContainerApproval>
      ) : (
        <Table basic="very" textAlign="left">
          <Header>
            <Row>
              <HeaderCell>Email</HeaderCell>
              <HeaderCell>Role</HeaderCell>
              <HeaderCell />
              <HeaderCell />
            </Row>
          </Header>

          <Body>
            {companyInvites.map((invite) => (
              <Row key={invite.id}>
                <Column>
                  <TextEmail>{invite.email}</TextEmail>
                </Column>

                <Column>
                  <Dropdown
                    onChange={(e, value) => handleChangeDropdown(invite.id, value)}
                    options={statusOptions}
                    defaultValue={DEFAULT_ROLE}
                  />
                </Column>

                <Column>
                  <Link onClick={() => handleApproveInvite(invite.id)}>
                    Approve
                  </Link>
                </Column>

                <Column>
                  <LinkDanger onClick={() => handleOpenInviteDialog(invite.id)}>
                    Remove
                  </LinkDanger>
                </Column>
              </Row>
            ))}
          </Body>
        </Table>
      )}

      <ConfirmDialog
        show={showConfirm}
        title="Are you sure you would like to remove this person?"
        warning="Warning: This cannot be undone"
        confirmText="Remove"
        cancelText="Cancel"
        handleConfirm={() => handleRemoveInvite()}
        handleClose={handleCloseDialog}
      />
    </>
  );
};

CompanyInvites.propTypes = {
  companyId: PropTypes.string.isRequired,
  data: PropTypes.shape({
    companyInvites: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        email: PropTypes.string,
        status: PropTypes.string,
        inviteType: PropTypes.string,
      }),
    ),
  }).isRequired,
};

export default CompanyInvites;
